import React, { useState } from 'react';
import { ChevronDown, Check, Plus, Minus, ShoppingCart, Calendar, Shield, Clock, Heart, Brain, Sparkles, Zap, Play, Headphones, Download } from 'lucide-react';
import PurchaseFormContent from '../../components/PurchaseStripeSimple';

// Placeholder components - replace with your actual implementations
const ScrollToSectionButton = ({ targetId, className, children }) => {
  const handleClick = () => {
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <button onClick={handleClick} className={className}>
      {children}
    </button>
  );
};

const HeroSection = () => {
    return (
      <section 
        className="relative flex items-center bg-beige-light"
        style={{ minHeight: 'var(--content-height)' }}
      >
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
        style={{ 
          backgroundImage: 'url("/images/products/il-nyasa/copertina.webp")',
        }}
      >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/75 to-beige-light"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-start h-full">
          <div className="md:hidden flex flex-col justify-end w-full h-full" style={{ minHeight: 'calc(var(--content-height) - 0rem)' }}>
            <div className="p-4 rounded-t-3xl mb-4">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3 text-green">🌿</span>
                <h1 className="h2 text-green">
                  RITROVA LA CALMA DENTRO DI TE
                </h1>
              </div>
              <p className="h4 text-text-dark mb-4">
                Con il Nyasa: il rituale che riequilibra corpo, mente e sistema nervoso
              </p>
              <ScrollToSectionButton 
                targetId="acquista"
                className="flex h4 items-center justify-center px-6 py-5 w-full bg-green text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
              >
                Accedi alla meditazione
                <ChevronDown className="ml-2" size={20} />
              </ScrollToSectionButton>
            </div>
          </div>
  
          <div className="hidden md:flex md:w-1/2 md:pr-8 flex-col">
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-3 text-green">🌿</span>
              <h1 className="h1 text-green">
                RITROVA LA CALMA DENTRO DI TE
              </h1>
            </div>
            <p className="h3 text-text-dark mb-6">
              Con il Nyasa: il rituale che riequilibra corpo, mente e sistema nervoso
            </p>
            <p className="body text-text-dark mt-6 mb-8">
              Una pratica antica e potente che ti riporta in uno stato di sicurezza, rilassamento e centratura profonda attraverso il suono e la presenza consapevole.
            </p>
            <ScrollToSectionButton 
              targetId="acquista"
              className="flex h4 items-center justify-center px-6 py-5 mt-4 bg-green text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
            >
              Accedi alla meditazione
              <ChevronDown className="ml-2" size={20} />
            </ScrollToSectionButton>
          </div>
          <div className="hidden md:block md:w-1/2 mt-8 md:mt-0">
            <div className="rounded-15 aspect-[3/4] w-full max-w-md mx-auto overflow-hidden shadow-lg">
              <img 
                src="/images/products/il-nyasa/copertina.webp" 
                alt="Nyasa - Il rituale della calma interiore"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    );
  };

const WhatIsNyasaSection = () => {
  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="h2 text-green mb-8">
              <span className="text-accent">🔍</span> Cos'è il Nyasa?
            </h2>
            <p className="body text-text-dark">
              Il <strong>Nyasa</strong> è una pratica che unisce <strong>suono e presenza consapevole</strong>. 
              Un rituale antico e potente che agisce sul sistema nervoso autonomo, riportandoti in uno stato di{' '}
              <strong>sicurezza, rilassamento e centratura profonda</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyItWorksSection = () => {
  const benefits = [
    {
      mechanism: "Stimolazione del nervo vago",
      effect: "Calma, rilassamento, riduzione dell'ansia",
      icon: <Brain className="w-6 h-6" />
    },
    {
      mechanism: "Vibrazione dei mantra",
      effect: "Riequilibrio cerebrale ed emozionale",
      icon: <Zap className="w-6 h-6" />
    },
    {
      mechanism: "Attivazione interocettiva",
      effect: "Ritorno alla presenza e alla fiducia nel corpo",
      icon: <Sparkles className="w-6 h-6" />
    },
    {
      mechanism: "Ritmo naturale",
      effect: "Stabilizzazione ormonale e sonno profondo",
      icon: <Clock className="w-6 h-6" />
    }
  ];

  return (
    <section className="bg-beige-light py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">✨</span> Perché funziona?
          </h2>
          <p className="body text-text-dark text-center mb-12">
            Ogni gesto, ogni vibrazione, ogni parola sacra ha un impatto preciso sul corpo:
          </p>
          
          <div className="bg-white rounded-15 shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="bg-green text-beige-light p-4 font-bold text-center">
                Meccanismo
              </div>
              <div className="bg-accent text-beige-light p-4 font-bold text-center">
                Effetto profondo
              </div>
            </div>
            {benefits.map((benefit, index) => (
              <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <div className="p-6 flex items-center">
                  <div className="mr-4 text-green">
                    {benefit.icon}
                  </div>
                  <span className="body text-text-dark">{benefit.mechanism}</span>
                </div>
                <div className="p-6">
                  <p className="body text-text-dark">{benefit.effect}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ForYouSection = () => {
  const points = [
    "Ti senti spesso **in allerta**, stanco/a o dissociato/a dal corpo",
    "Vivi ansia, insonnia, tensione o stanchezza cronica",
    "Cerchi una pratica **dolce ma trasformativa**",
    "Vuoi riconnetterti al tuo **piacere, radicamento e ciclicità interiore**",
    "Desideri imparare a **regolarti dall'interno**, senza forzare nulla"
  ];

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">👇</span> Questa meditazione è per te se:
          </h2>
          
          <div className="space-y-4">
            {points.map((point, index) => (
              <div key={index} className="flex items-start p-4 bg-beige-light rounded-15">
                <div className="mr-4 pt-1 text-green">
                  <Check size={24} />
                </div>
                <p className="body text-text-dark">
                  {point.split(/\*\*(.*?)\*\*/).map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatYouGetSection = () => {
  const features = [
    {
      icon: <Play className="w-8 h-8" />,
      title: "Registrazione completa",
      description: "30 minuti di pratica guidata del Nyasa"
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: "Accesso immediato",
      description: "Disponibile subito nella tua area riservata"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Pratica quando vuoi",
      description: "Riascolta illimitatamente, al tuo ritmo"
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: "Sempre con te",
      description: "Accessibile da qualsiasi dispositivo"
    }
  ];

  return (
    <section className="bg-green py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="h2 text-beige-light text-center mb-12">
            <span className="text-beige-light">🎁</span> Cosa ricevi:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-beige-light/10 backdrop-blur-sm p-6 rounded-15 text-center">
                <div className="text-beige-light mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="h4 text-beige-light mb-2">{feature.title}</h3>
                <p className="body_tile text-text-light">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CourseDetailsSection = () => {
  const details = [
    { icon: "⏱️", title: "30 minuti di pratica", description: "La durata perfetta per una trasformazione profonda" },
    { icon: "🔐", title: "Accesso privato", description: "Disponibile nella tua area riservata personale" },
    { icon: "🌙", title: "Pratica quotidiana", description: "Ideale per la tua routine di benessere mattutina o serale" }
  ];

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">🧘‍♀️</span> Come funziona:
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {details.map((detail, index) => (
              <div key={index} className="text-center p-6 bg-beige-light rounded-15">
                <div className="text-4xl mb-4">{detail.icon}</div>
                <h3 className="h4 text-green mb-2">{detail.title}</h3>
                <p className="body_tile text-text-dark">{detail.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Silvia, 42 anni",
      text: "Non pensavo che bastasse così poco per sentirmi finalmente a casa nel mio corpo."
    },
    {
      name: "Luca, 37 anni",
      text: "Più di una pratica: è un ritorno alla mia verità più profonda."
    }
  ];

  return (
    <section className="bg-beige-light py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">💬</span> Dicono del Nyasa:
          </h2>
          
          <div className="space-y-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-15 shadow-md">
                <p className="body text-text-dark italic mb-4">"{testimonial.text}"</p>
                <p className="h4 text-green">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CheckoutSection = () => {
  const productId = "f7f80353-7086-4ef7-81c1-2bf91258a7ed"; // Replace with your actual product ID
  
  return (
    <section id="acquista" className="py-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-beige-light to-transparent"></div>
      
      {/* Highlight effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-green/20 to-accent/15 pointer-events-none"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Price banner */}
          <div className="bg-gradient-to-r from-green to-accent py-6 px-6 text-center text-white">
            <h2 className="h2 text-beige-light mb-2">💸 Accedi alla registrazione: €12</h2>
            <p className="body text-text-light">Un investimento nella tua pace interiore. Accesso immediato e illimitato.</p>
          </div>
          
          <div className="p-8 md:p-12">
            {/* CTA */}
            <div className="text-center mb-8">
              <h3 className="h2 text-green mb-4">
                <span className="text-accent">🔔</span> Sei pronta/o a tornare in te?
              </h3>
              <p className="h3 text-text-dark mb-6">La registrazione della meditazione del Nyasa ti aspetta</p>
              <div className="bg-beige-light p-4 rounded-15 mb-6 max-w-2xl mx-auto">
                <p className="body text-text-dark">
                  <strong>Come funziona:</strong> Dopo l'acquisto, riceverai accesso immediato alla tua area riservata 
                  dove troverai la registrazione completa della pratica del Nyasa. Potrai riascoltarla tutte le volte 
                  che vorrai, creando il tuo rituale personale di calma e centratura.
                </p>
              </div>
            </div>
            
            {/* Purchase form */}
            <div className="bg-gradient-to-r from-green/25 to-accent/20 p-6 rounded-xl shadow-md mb-8">
              <PurchaseFormContent productId={productId} />
            </div>
            
            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center bg-beige-light px-4 py-2 rounded-lg">
                <Play className="text-green mr-2" size={20} />
                <p className="body_tile text-text-dark">Accesso immediato</p>
              </div>
              <div className="flex items-center bg-beige-light px-4 py-2 rounded-lg">
                <Shield className="text-green mr-2" size={20} />
                <p className="body_tile text-text-dark">Pagamento sicuro</p>
              </div>
              <div className="flex items-center bg-beige-light px-4 py-2 rounded-lg">
                <Headphones className="text-green mr-2" size={20} />
                <p className="body_tile text-text-dark">Riascolta illimitatamente</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Define color variables using exact values from Tailwind config
const style = `
  .text-green { color: #89AD4C; }
  .bg-green { background-color: #89AD4C; }
  .hover\\:bg-green:hover { background-color: #89AD4C; }
  .text-green-light { color: #c5f96d; }
  .bg-green-light { background-color: #c5f96d; }
  .text-green-dark { color: #2f4522; }
  .bg-green-dark { background-color: #2f4522; }
  .hover\\:bg-accent:hover { background-color: #ff9f1c; }
  .text-accent { color: #ff9f1c; }
  .bg-accent { background-color: #ff9f1c; }
  .text-beige-light { color: #fffce4; }
  .bg-beige-light { background-color: #fffce4; }
  .text-beige { color: #EDE6D4; }
  .bg-beige { background-color: #EDE6D4; }
  .text-beige-dark { color: #D2A750; }
  .bg-beige-dark { background-color: #D2A750; }
  .text-text-dark { color: #2f4522; }
  .text-text-light { color: #fffce4; }
  .rounded-15 { border-radius: 15px; }
  .rounded-30 { border-radius: 30px; }
`;

const Page = () => {
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="flex flex-col">
        <HeroSection />
        <WhatIsNyasaSection />
        <WhyItWorksSection />
        <ForYouSection />
        <WhatYouGetSection />
        <CourseDetailsSection />
        <TestimonialsSection />
        <CheckoutSection />
      </div>
    </>
  );
};

export default Page;